import Box from '@mui/material/Box'
import { grey } from '@mui/material/colors'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { Camera } from '@styled-icons/heroicons-solid'
import React from 'react'

import { ReactComponent as MountainSolidIcon } from 'assets/MountainSolid.svg'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import { getTransformDisposalData } from 'components/work-order/details/disposal-ticket/settings'
import { formatCurrency } from 'utils/price'
import { normalizeCasing } from 'utils/string'

export type DisposalSite = {
  id: string
  name?: string
  disposalSiteMaterial?: { id: string; tippingFee?: number; material?: { materialType?: string; photoRequired?: boolean } }[]
}

export type DisposalMaterial = {
  disposalSiteMaterialId?: string
  unitOfMeasure?: string
  quantity?: number
  unitPriceDollars?: number
}

type Props = {
  disposalTicketTotalCents?: number
  disposalTickets?: unknown[]
  disposalSites?: DisposalSite[]
  onOpenDisposalTicketDialog?: () => void
}

const DisposalTicketRow: React.FC<Props> = ({
  disposalTicketTotalCents = 0,
  disposalTickets = [],
  disposalSites = [],
  onOpenDisposalTicketDialog,
}) => {
  const theme = useTheme()
  const disposalData = getTransformDisposalData(disposalTickets)
  const selectedDisposal = disposalSites.find(site => site.id === (disposalData?.disposalSiteId ?? ''))
  const materials: DisposalMaterial[] = disposalData?.materials ?? []

  return (
    <>
      <Box
        alignItems="center"
        display="flex"
        flexWrap="wrap"
        gap={1}
        justifyContent="space-between"
        width="100%"
        onClick={onOpenDisposalTicketDialog}
      >
        <Box alignItems="center" display="flex" gap={1}>
          <MountainSolidIcon />
          <Typography color="textPrimary" fontWeight={700} variant="caption">
            {disposalData?.ticketNumber ?? ''}
          </Typography>
          <Divider flexItem orientation="vertical" sx={{ borderColor: 'text.primary' }} />
          <Typography color="textPrimary" fontWeight={700} variant="caption">
            {selectedDisposal?.name ?? ''}
          </Typography>
          <Divider flexItem orientation="vertical" sx={{ borderColor: 'text.primary' }} />
          <Typography fontWeight={700} variant="h6">
            <HHDisplayMoney value={disposalTicketTotalCents} />
          </Typography>
        </Box>
        <Box alignItems="center" display="flex" gap={1}>
          {materials.map(material => {
            const selectedMaterial = selectedDisposal?.disposalSiteMaterial?.find(r => r.id === material.disposalSiteMaterialId)
            const materialType = selectedMaterial?.material?.materialType ?? ''
            const measureUnit = normalizeCasing(material.unitOfMeasure ?? '')
            const photoRequired = selectedMaterial?.material?.photoRequired ?? false
            const quantity = material.quantity ?? 0
            const unitPriceDollars = material.unitPriceDollars ?? 0

            return (
              <Box
                key={material.disposalSiteMaterialId}
                alignItems="center"
                border="1px solid"
                borderColor={grey[400]}
                borderRadius={1}
                display="flex"
                p={0.5}
              >
                <Typography fontWeight={500} variant="subtitle1">
                  {`${materialType} | ${quantity} ${measureUnit} | ${formatCurrency(unitPriceDollars)}`}
                  {photoRequired && (
                    <>
                      {` | `}
                      <Camera color={theme.palette.action.active} height={16} width={16} />
                    </>
                  )}
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Box>
    </>
  )
}

export default DisposalTicketRow
